import React, { useState, useEffect, useCallback, useRef } from "react";
import { useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FaPlay, FaPause, FaSearch, FaCalendarAlt, FaImage, FaVideo, FaShoppingCart, FaTimes, FaDownload, FaEdit, FaInfoCircle, FaShare } from 'react-icons/fa';
import { motion, AnimatePresence } from "framer-motion";
import { useUserDetails } from '../context/UserContext';

/**
 * Dashboard Screen
 * A Unified Multi‑Channel hub that aggregates posts, notifications, and analytics
 * from all connected platforms. It provides quick filters, a status overview, and a
 * content library displaying preview thumbnails, scheduling details, and basic analytics.
 */

// Custom Header Component for Dashboard
const DashboardHeader = ({ creditInfo }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { userDetails } = useUserDetails();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleUserProfileClick = () => {
    window.open(process.env.REACT_APP_USER_PROFILE, '_blank');
    setIsDropdownOpen(false);
  };

  const handleLogout = () => {
    signOut();
    setIsDropdownOpen(false);
  };

  const handleBuyCredits = () => {
    navigate('/pricing');
    setIsDropdownOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full">
      {/* Glass Header Container */}
      <div className="glass-effect border-b border-white/5 backdrop-blur-2xl bg-black/10">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 lg:px-6 py-1">
          <div className="flex items-center justify-between">
            
            {/* Left Section - Logo and Taco */}
            {!isSignedIn && (
              <div className="flex items-center gap-1 lg:gap-1.5">
                {/* Logo */}
                <img 
                  src="/logo.png" 
                  alt="Taco Logo" 
                  className="w-5 h-5 sm:w-6 sm:h-6"
                />
                
                {/* Taco Text */}
                <h1 className="text-sm sm:text-base lg:text-lg font-bold">
                  <span className="gradient-text">Taco</span>
                </h1>
              </div>
            )}

            {/* Right Section - Auth Buttons or Profile */}
            <div className="flex items-center gap-1 sm:gap-1.5 ml-auto">
              {isLoaded && (
                <>
                  {isSignedIn ? (
                    /* Profile Section with Dropdown - When Signed In */
                    <div className="relative" ref={dropdownRef}>
                      <div 
                        onClick={handleProfileClick}
                        className="flex items-center gap-1 sm:gap-1.5 cursor-pointer group transition-all duration-300 px-1 sm:px-1.5 md:px-2 py-0.5 rounded-full hover:bg-white/10"
                      >
                        {/* User Avatar */}
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full overflow-hidden border border-gray-300 group-hover:border-white transition-colors duration-300">
                          {user?.imageUrl ? (
                            <img 
                              src={user.imageUrl} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* User Name and Plan Info */}
                        <div className="flex flex-col items-start">
                          <span className="text-xs font-medium text-gray-200 group-hover:text-white transition-colors duration-300 leading-tight">
                            {user?.firstName} {user?.lastName}
                          </span>
                          {/* Plan info - more compact on mobile */}
                          <div className="flex items-center gap-0.5 sm:gap-1 mt-0 leading-tight">
                            {/* <span className="text-xs text-green-400 font-medium">
                              {(userDetails?.usage?.account_type || 'Trial').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            <span className="text-xs text-gray-400">•</span> */}
                            <span className={`text-xs font-medium ${parseFloat(creditInfo?.credit_left_amt || userDetails?.credit_left_amt || '0') > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {parseFloat(creditInfo?.credit_left_amt || userDetails?.credit_left_amt || '0').toFixed(2)} credits
                            </span>
                          </div>
                        </div>
                        
                        {/* Dropdown Arrow */}
                        <svg className={`w-3 h-3 text-gray-400 group-hover:text-gray-200 transition-all duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className="absolute right-0 top-full mt-2 w-64 sm:w-72 bg-gray-900/95 border border-gray-700/50 rounded-xl shadow-2xl z-50 overflow-hidden backdrop-blur-xl">
                          {/* User Info Header */}
                          <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-700/50">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-gray-600">
                                {user?.imageUrl ? (
                                  <img 
                                    src={user.imageUrl} 
                                    alt="Profile" 
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-white font-medium text-sm">
                                  {user?.firstName} {user?.lastName}
                                </p>
                                <p className="text-gray-400 text-xs">
                                  {user?.primaryEmailAddress?.emailAddress}
                                </p>
                              </div>
                            </div>
                          </div>



                          {/* Action Buttons */}
                          <div className="py-1 sm:py-2">
                            <button
                              onClick={() => {
                                navigate('/billing');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <FaShoppingCart className="text-gray-300" />
                              Buy More Credits
                            </button>
                            <button
                              onClick={() => {
                                window.open('mailto:manesh.pai@visualtaco.com?subject=Hi there!', '_blank');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Contact Us
                            </button>
                            <button
                              onClick={handleUserProfileClick}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              User Profile
                            </button>
                            <button
                              onClick={handleLogout}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Log out
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Log In and Sign Up Buttons - When Not Signed In */
                    <>
                      {/* Log In Button */}
                      <a 
                        href="/login" 
                        className="text-gray-300 hover:text-white transition-colors duration-300 px-1.5 sm:px-2 py-0.5 text-xs font-medium inline-block"
                      >
                        Log In
                      </a>
                      
                      {/* Sign Up Button */}
                      <a 
                        href="/sign-up" 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-2 sm:px-3 py-0.5 rounded-full text-xs font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block"
                      >
                        Sign Up
                      </a>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top glow effect */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
    </header>
  );
};

// Lazy Image Component with Intersection Observer
const LazyImage = ({ src, alt, className, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={`relative ${className}`} onClick={onClick}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-800 animate-pulse rounded-lg" />
      )}
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover rounded-lg transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
};

// Lazy Video Component with Intersection Observer
const LazyVideo = ({ src, poster, className }) => {
  const [isInView, setIsInView] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        } else {
          setIsPlaying(false);
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      },
      { rootMargin: '50px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {isInView && (
        <>
          <video
            ref={videoRef}
            src={src}
            poster={poster}
            className="w-full h-full object-cover rounded-lg"
            playsInline
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />
          <div
            className="absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300 bg-black bg-opacity-0 hover:bg-opacity-20"
            onClick={togglePlay}
          >
            <div className={`bg-white bg-opacity-30 backdrop-blur-sm rounded-full p-4 transition-all duration-300 ${
              isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'
            }`}>
              {isPlaying ? (
                <FaPause className="text-white text-2xl" />
              ) : (
                <FaPlay className="text-white text-2xl ml-1" />
              )}
            </div>
          </div>
        </>
      )}
      {!isInView && (
        <div className="w-full h-full bg-gray-800 animate-pulse rounded-lg" />
      )}
    </div>
  );
};

// Lightbox Modal Component
const LightboxModal = ({ isOpen, onClose, content, type }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen && type === 'video' && videoRef.current) {
      videoRef.current.play();
    }
  }, [isOpen, type]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
        onClick={onClose}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 text-white hover:text-gray-300 transition-colors"
        >
          <FaTimes className="text-3xl" />
        </button>
        <div
          className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          {type === 'image' ? (
            <img
              src={content}
              alt="Full size"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          ) : (
            <video
              ref={videoRef}
              src={content}
              className="max-w-full max-h-full object-contain rounded-lg"
              controls
              playsInline
              autoPlay
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

function Dashboard() {
  // Pagination and posts state
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    date: "",
    search: "",
    type: "all" // all, image, video
  });

  // Lightbox state
  const [lightbox, setLightbox] = useState({
    isOpen: false,
    content: null,
    type: null
  });

  // Info modal state
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    post: null
  });

  // Get session variable userDetails
  const [userDetails, setUserDetails] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userDetails")) || {};
    } catch (error) {
      console.error("Error parsing userDetails:", error);
      return {};
    }
  });

  // Credit info state for header
  const [creditInfo, setCreditInfo] = useState({ total_cost: 0, credit_left_amt: 0 });

  // Track per-post download in-progress state
  const [downloadingById, setDownloadingById] = useState({});

  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  // Refs for infinite scroll
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Initial load
  useEffect(() => {
    if (user) {
      fetchPosts(1, true);
    }
  }, [user]);

  // Setup infinite scroll observer
  useEffect(() => {
    if (isLoading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingRef.current) {
          fetchPosts(page + 1, false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [page, hasMore, isLoading]);

  const fetchPosts = useCallback(async (pageNum, isInitial = false) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    if (isInitial) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const token = await getToken();
      const formDataToSend = new FormData();
      formDataToSend.append("userDetails", JSON.stringify(userDetails || {}));
      formDataToSend.append("page", pageNum.toString());
      formDataToSend.append("page_size", "20");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard_data`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.posts && Array.isArray(data.posts)) {
        if (isInitial) {
          setPosts(data.posts);
        } else {
          setPosts(prev => [...prev, ...data.posts]);
        }
        
        setPage(pageNum);
        setHasMore(data.pagination?.has_more || false);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [user, getToken, userDetails]);

  // Filter posts based on filters
  const filteredPosts = posts.filter(post => {
    // Filter by type
    if (filters.type !== 'all' && post.type !== filters.type) {
      return false;
    }

    // Filter by date
    if (filters.date) {
      const selectedDate = new Date(filters.date);
      selectedDate.setHours(0, 0, 0, 0);
      
      const postDate = new Date(post.generatedTime);
      postDate.setHours(0, 0, 0, 0);
      
      if (postDate.getTime() !== selectedDate.getTime()) {
        return false;
      }
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const filenameMatch = post.filename?.toLowerCase().includes(searchLower);
      const idMatch = post.id?.toLowerCase().includes(searchLower);
      
      return filenameMatch || idMatch;
    }

    return true;
  });

  // Update filter state when any filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  // Open lightbox
  const openLightbox = (content, type) => {
    setLightbox({
      isOpen: true,
      content,
      type
    });
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightbox({
      isOpen: false,
      content: null,
      type: null
    });
  };

  // Open info modal
  const openInfoModal = (post) => {
    setInfoModal({
      isOpen: true,
      post
    });
  };

  // Close info modal
  const closeInfoModal = () => {
    setInfoModal({
      isOpen: false,
      post: null
    });
  };

  // Format value for display
  const formatInfoValue = (value, key = '') => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') {
      // Format cost values - show as is without unnecessary decimals
      if (value === 0) return 'Free';
      // Check if it's an integer
      if (Number.isInteger(value)) {
        return value.toString();
      }
      // For floats, remove trailing zeros
      return value.toString();
    }
    if (typeof value === 'string') {
      // Don't truncate prompts - show full text
      if (key === 'prompt') return value;
      // Truncate other long strings
      if (value.length > 50) return value.substring(0, 50) + '...';
      return value;
    }
    return String(value);
  };

  // Get display label for info key
  const getInfoLabel = (key) => {
    const labelMap = {
      'total_cost': 'Cost',
      'ai_model_name': 'Model',
      'aspect_ratio': 'Aspect Ratio',
      'disable_safety_checker': 'Safety Checker',
      'go_fast': 'Fast Mode',
      'megapixels': 'Megapixels',
      'num_inference_steps': 'Inference Steps',
      'num_outputs': 'Outputs',
      'output_format': 'Format',
      'output_quality': 'Quality',
      'prompt': 'Prompt'
    };
    return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) return '';
      
      // Format as "Feb 27, 2025, 1:52 PM"
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      console.error("Error formatting date:", e);
      return '';
    }
  };

  // Best-effort cross-device download: uses Web Share API with files on mobile/tablets,
  // falls back to anchor download for desktops or unsupported browsers
  const getFilenameFromUrl = (url, fallbackName) => {
    try {
      const pathname = new URL(url).pathname || '';
      const lastSegment = pathname.split('/').pop() || '';
      const cleaned = (lastSegment.split('?')[0] || lastSegment) || '';
      return decodeURIComponent(cleaned) || fallbackName;
    } catch (e) {
      return fallbackName;
    }
  };

  const getFileExtension = (filename, isVideo) => {
    const match = filename?.match(/\.([a-zA-Z0-9]+)(?:$|\?)/);
    let ext = match?.[1]?.toLowerCase();
    if (!ext) {
      ext = isVideo ? 'mp4' : 'png';
    }
    return ext;
  };

  const inferMimeType = (ext, isVideo) => {
    const imageMimeMap = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif' };
    const videoMimeMap = { mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' };
    return (isVideo ? videoMimeMap[ext] : imageMimeMap[ext]) || (isVideo ? 'video/mp4' : 'image/png');
  };

  const downloadViaAnchor = (blob, filename) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const handleDownload = async (post) => {
    try {
      const isVideo = post.type === 'video';
      const mediaUrl = post.video_signed_url || post.signed_url;
      if (!mediaUrl) {
        toast.error('No media to download');
        return;
      }

      const idKey = post.id || mediaUrl;
      setDownloadingById(prev => ({ ...prev, [idKey]: true }));

      const fallbackName = post.filename || (isVideo ? 'video' : 'image');
      let filename = getFilenameFromUrl(mediaUrl, fallbackName);
      const ext = getFileExtension(filename, isVideo);
      if (!filename.toLowerCase().endsWith(`.${ext}`)) {
        filename = `${filename}.${ext}`;
      }

      // Check if mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
        || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);

      // Use backend proxy to avoid CORS and preserve auth
      const token = await getToken();
      const proxyUrl = `${process.env.REACT_APP_API_URL}/download_media?url=${encodeURIComponent(mediaUrl)}&filename=${encodeURIComponent(filename)}`;
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const blob = await response.blob();
      const mimeType = blob.type || inferMimeType(ext, isVideo);
      const normalizedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });

      // On mobile, use Web Share API to allow saving to gallery
      if (isMobile && navigator.share) {
        try {
          const file = new File([normalizedBlob], filename, { type: mimeType });
          const shareData = {
            title: isVideo ? 'Generated Video' : 'Generated Content',
            text: isVideo ? 'Download your generated video' : 'Download your generated content',
            files: [file]
          };
          
          // Check if files can be shared
          if (navigator.canShare && navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        } catch (err) {
          // If share fails or is cancelled, fall through to download
          if (err.name === 'AbortError') {
            return; // User cancelled, don't show error
          }
          console.error('Share failed, falling back to download:', err);
        }
      }

      downloadViaAnchor(normalizedBlob, filename);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Please try again.');
    } finally {
      const isVideo = post.type === 'video';
      const mediaUrl = post.video_signed_url || post.signed_url;
      const idKey = post.id || mediaUrl;
      setDownloadingById(prev => ({ ...prev, [idKey]: false }));
    }
  };

  const handleShare = async (post) => {
    try {
      const isVideo = post.type === 'video';
      const mediaUrl = post.video_signed_url || post.signed_url;
      if (!mediaUrl) {
        toast.error('No media to share');
        return;
      }

      // Check if Web Share API is supported
      if (navigator.share) {
        try {
          // Use backend proxy to avoid CORS and preserve auth
          const token = await getToken();
          const fallbackName = post.filename || (isVideo ? 'video' : 'image');
          let filename = getFilenameFromUrl(mediaUrl, fallbackName);
          const ext = getFileExtension(filename, isVideo);
          if (!filename.toLowerCase().endsWith(`.${ext}`)) {
            filename = `${filename}.${ext}`;
          }

          const proxyUrl = `${process.env.REACT_APP_API_URL}/download_media?url=${encodeURIComponent(mediaUrl)}&filename=${encodeURIComponent(filename)}`;
          const response = await fetch(proxyUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          if (!response.ok) throw new Error(`Share failed (${response.status})`);
          
          const blob = await response.blob();
          const mimeType = blob.type || inferMimeType(ext, isVideo);
          const normalizedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });
          const file = new File([normalizedBlob], filename, { type: mimeType });
          
          // Get content text from post if available
          const contentText = post.content || post.description || post.prompt || '';
          
          const shareData = {
            title: isVideo ? 'Generated Video' : 'Generated Content',
            text: contentText ? contentText.substring(0, 100) : (isVideo ? 'Check out this cool AI video from Taco!' : 'Check out this cool AI image from Taco!'),
            files: [file]
          };

          // Check if files can be shared
          if (navigator.canShare && navigator.canShare(shareData)) {
            await navigator.share(shareData);
          } else {
            // Fallback to sharing just the URL if files aren't supported
            await navigator.share({
              title: isVideo ? 'Generated Video' : 'Generated Content',
              text: contentText ? contentText.substring(0, 100) : (isVideo ? 'Check out this cool AI video from Taco!' : 'Check out this cool AI image from Taco!'),
              url: mediaUrl
            });
          }
        } catch (err) {
          // User cancelled or error occurred
          if (err.name !== 'AbortError') {
            console.error('Error sharing:', err);
            toast.error('Share failed. Please try again.');
          }
        }
      } else {
        // Fallback for browsers that don't support Web Share API
        console.log('Web Share API not supported');
        toast.error('Sharing is not supported on this device');
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Share failed. Please try again.');
    }
  };

  // Edit handler: stash image URL and navigate to CreateImage page
  const handleEdit = (post) => {
    try {
      if (post.type === 'video') {
        toast.error('Editing is available for images only');
        return;
      }
      const mediaUrl = post.signed_url;
      if (!mediaUrl) {
        toast.error('No image to edit');
        return;
      }
      const filename = post.filename || 'image';
      try {
        sessionStorage.setItem('pendingEditImageUrl', mediaUrl);
        sessionStorage.setItem('pendingEditImageName', filename);
      } catch (_) {}
      navigate('/image');
    } catch (e) {
      console.error('Edit navigation error:', e);
      toast.error('Unable to open editor');
    }
  };

  // Edit-to-Video handler: stash image URL and navigate to CreateVideo page
  const handleEditToVideo = (post) => {
    try {
      if (post.type === 'video') {
        toast.error('This option is for images only');
        return;
      }
      const mediaUrl = post.signed_url;
      if (!mediaUrl) {
        toast.error('No image to use for video');
        return;
      }
      const filename = post.filename || 'image';
      try {
        sessionStorage.setItem('pendingEditImageUrl', mediaUrl);
        sessionStorage.setItem('pendingEditImageName', filename);
      } catch (_) {}
      navigate('/video');
    } catch (e) {
      console.error('Edit-to-video navigation error:', e);
      toast.error('Unable to open video creator');
    }
  };

  // Render content card with lazy loading
  const renderContentCard = (post) => {
    const isVideo = post.type === 'video';
    const mediaUrl = post.video_signed_url || post.signed_url;

    return (
      <motion.div
        key={post.id}
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="group relative"
      >
        {/* Media Container */}
        <div 
          className="relative aspect-square w-full overflow-hidden rounded-2xl bg-gray-900 border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-2xl"
          onClick={() => openLightbox(mediaUrl, isVideo ? 'video' : 'image')}
        >

          {/* Download Button (visible on touch devices, on-hover on larger screens) */}
          <button
            type="button"
            aria-label="Download"
            title="Download"
            onClick={(e) => { e.stopPropagation(); handleDownload(post); }}
            disabled={!!downloadingById[post.id || (post.video_signed_url || post.signed_url)]}
            className="absolute top-2 right-2 z-10 rounded-full p-2 bg-black/50 hover:bg-black/70 text-white shadow-md transition-opacity duration-200 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {downloadingById[post.id || (post.video_signed_url || post.signed_url)] ? (
              <span className="relative inline-flex items-center justify-center">
                <span className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                <span className="sr-only">Downloading</span>
              </span>
            ) : (
              <FaDownload className="text-sm" />
            )}
          </button>

          {/* Share Button (visible only on mobile devices) */}
          <button
            type="button"
            aria-label="Share"
            title="Share"
            onClick={(e) => { e.stopPropagation(); handleShare(post); }}
            className="absolute top-2 right-11 z-10 rounded-full p-2 bg-black/50 hover:bg-black/70 text-white shadow-md transition-opacity duration-200 opacity-100 lg:hidden"
          >
            <FaShare className="text-sm" />
          </button>

          {/* Edit Button (images only) - moved to left side */}
          {post.type !== 'video' && (
            <button
              type="button"
              aria-label="Edit Image"
              title="Edit Image"
              onClick={(e) => { e.stopPropagation(); handleEdit(post); }}
              className="absolute top-2 left-2 z-10 rounded-full p-2 bg-black/50 hover:bg-black/70 text-white shadow-md transition-opacity duration-200 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
            >
              <FaEdit className="text-sm" />
            </button>
          )}

          {/* Create Video Button (images only) - moved to left side */}
          {post.type !== 'video' && (
            <button
              type="button"
              aria-label="Create Video"
              title="Create Video"
              onClick={(e) => { e.stopPropagation(); handleEditToVideo(post); }}
              className="absolute top-2 left-11 z-10 rounded-full p-2 bg-black/50 hover:bg-black/70 text-white shadow-md transition-opacity duration-200 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
            >
              <FaVideo className="text-sm" />
            </button>
          )}


          {/* Media Content */}
          {isVideo ? (
            <LazyVideo
              src={mediaUrl}
              className="w-full h-full"
            />
          ) : (
            <LazyImage
              src={mediaUrl}
              alt={post.filename || 'Content'}
              className="w-full h-full"
            />
          )}

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
              {/* <p className="text-xs font-medium truncate mb-1">
                {post.filename || 'Untitled'}
              </p> */}
              <p className="text-xs text-gray-300">
                {formatDate(post.generatedTime)}
              </p>
            </div>
          </div>

          {/* Downloading indicator badge */}
          {downloadingById[post.id || (post.video_signed_url || post.signed_url)] && (
            <div className="absolute bottom-2 right-2 z-10">
              <div className="px-2 py-1 rounded-full bg-black/60 text-white text-[10px] sm:text-xs flex items-center gap-1 backdrop-blur">
                <span className="w-3 h-3 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                <span>Downloading…</span>
              </div>
            </div>
          )}
        </div>

        {/* Info Section - Subtle display below the media */}
        {post.info && (
          <div className="mt-2 px-1">
            <div className="flex items-center justify-between text-xs text-gray-400">
              <div className="flex items-center gap-3 flex-wrap">
                {/* Show key fields */}
                {post.info.total_cost !== undefined && (
                  <span className="flex items-center gap-1">
                    <span className="text-gray-500">Cost:</span>
                    <span className="text-gray-300 font-medium">
                      {post.info.total_cost === 0 ? 'Free' : formatInfoValue(post.info.total_cost)}
                    </span>
                  </span>
                )}
                {/* Show generated time */}
                {post.generatedTime && (
                  <span className="flex items-center gap-1">
                    <span className="text-gray-500">Time:</span>
                    <span className="text-gray-300 font-medium">
                      {formatDate(post.generatedTime)}
                    </span>
                  </span>
                )}
              </div>
              {/* More Info Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openInfoModal(post);
                }}
                className="flex items-center gap-1 text-gray-400 hover:text-purple-400 transition-colors duration-200 px-2 py-1 rounded hover:bg-gray-800/50"
                title="View more details"
              >
                <FaInfoCircle className="text-[10px]" />
                <span className="text-[10px]">More</span>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  // Render a loading message if data is still being fetched
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-20 w-64 h-64 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        </div>
        
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mb-4 mx-auto"></div>
          <div className="text-white text-lg">Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black relative">
      {/* Header */}
      <DashboardHeader creditInfo={creditInfo} />
      
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-20 w-64 h-64 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 pt-20 sm:pt-24 pb-8">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 sm:mb-6"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Your <span className="gradient-text">Content Library</span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-base mt-1">
            Browse and manage your creative content
          </p>
        </motion.div>

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-effect rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 border border-gray-700/50"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Type Filter */}
            <div className="flex-1">
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-800/70 border border-gray-600/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
              >
                <option value="all">All Content</option>
                <option value="image">Images Only</option>
                <option value="video">Videos Only</option>
              </select>
            </div>

            {/* Date Filter */}
            <div className="flex-1">
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="date"
                  name="date"
                  value={filters.date}
                  onChange={handleFilterChange}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2 sm:py-2.5 bg-gray-800/70 border border-gray-600/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
                />
              </div>
            </div>

            {/* Search Filter */}
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  name="search"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2 sm:py-2.5 bg-gray-800/70 border border-gray-600/50 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Content Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {filteredPosts.length === 0 && !isLoading ? (
            <div className="glass-effect rounded-2xl p-8 sm:p-12 text-center border border-gray-700/50">
              <div className="text-gray-400 text-base sm:text-lg mb-2">No content found</div>
              <p className="text-gray-500 text-sm sm:text-base">
                {filters.search || filters.date || filters.type !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Start creating awesome content to see it here!'}
              </p>
            </div>
          ) : (
            <>
              {/* Masonry Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {filteredPosts.map(renderContentCard)}
              </div>

              {/* Loading More Indicator */}
              {hasMore && (
                <div ref={loadMoreRef} className="mt-8 flex justify-center">
                  {isLoadingMore && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">Loading more...</span>
                    </div>
                  )}
                </div>
              )}

              {/* End of Content Message */}
              {!hasMore && filteredPosts.length > 0 && (
                <div className="mt-8 text-center text-gray-500 text-sm">
                  You've reached the end! 🎉
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightbox.isOpen}
        onClose={closeLightbox}
        content={lightbox.content}
        type={lightbox.type}
      />

      {/* Info Details Modal */}
      {infoModal.isOpen && infoModal.post && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
            onClick={closeInfoModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-900 rounded-2xl border border-gray-700/50 max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
                <h3 className="text-lg font-semibold text-white">Generation Details</h3>
                <button
                  onClick={closeInfoModal}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
                <div className="space-y-4">
                  {/* Show generated time at the top */}
                  {infoModal.post.generatedTime && (
                    <div className="flex justify-between items-start gap-4 py-2 border-b border-gray-800/50">
                      <span className="text-sm text-gray-400 flex-shrink-0">
                        Generated Time:
                      </span>
                      <span className="text-sm text-gray-200 text-right break-words flex-1">
                        {formatDate(infoModal.post.generatedTime)}
                      </span>
                    </div>
                  )}
                  
                  {infoModal.post.info && (
                    <>
                      {/* Render all info fields generically - sorted alphabetically */}
                      {Object.entries(infoModal.post.info)
                        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                        .map(([key, value]) => {
                      // Skip rendering nested objects directly - handle them separately
                      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        return (
                          <div key={key} className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-300 capitalize">
                              {getInfoLabel(key)}
                            </h4>
                            <div className="pl-4 space-y-1 border-l-2 border-gray-700/50">
                              {Object.entries(value)
                                .sort(([nestedKeyA], [nestedKeyB]) => nestedKeyA.localeCompare(nestedKeyB))
                                .map(([nestedKey, nestedValue]) => (
                                <div key={nestedKey} className="flex justify-between items-start gap-4">
                                  <span className="text-xs text-gray-400 flex-shrink-0">
                                    {getInfoLabel(nestedKey)}:
                                  </span>
                                  <span className={`text-xs text-gray-300 ${nestedKey === 'prompt' ? 'text-left' : 'text-right'} break-words flex-1`}>
                                    {formatInfoValue(nestedValue, nestedKey)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div key={key} className={`flex ${key === 'prompt' ? 'flex-col' : 'justify-between items-start'} gap-2 py-2 border-b border-gray-800/50 last:border-0`}>
                          <span className="text-sm text-gray-400 flex-shrink-0">
                            {getInfoLabel(key)}:
                          </span>
                          <span className={`text-sm text-gray-200 ${key === 'prompt' ? 'text-left' : 'text-right'} break-words flex-1`}>
                            {formatInfoValue(value, key)}
                          </span>
                        </div>
                      );
                    })}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

export default Dashboard;
